import { FunctionComponent } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Grouped (clustered) bar chart — like GraphBarChartStacked but the series are NOT
 * stacked (no `stack` key), so bars render side-by-side per x-axis category.
 * Used for month this-year-vs-last-year comparisons.
 */
export const GraphBarChartGrouped: FunctionComponent<{
  title: string;
  tooltip?: string;
  dataset: Array<{}>;
  series: Array<{ dataKey: string; label: string }>;
  colors: string[];
  xAxisDataKey?: string;
  chartHeight?: number;
  paperHeight?: number | string;
}> = ({ title, tooltip, dataset, series, colors, xAxisDataKey = 'month', chartHeight = 320, paperHeight = '100%' }) => {
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const seriesWithFormat = series.map((s) => ({
    ...s,
    valueFormatter: (value: number | null) => (value == null ? '' : `${fmtTco2e.format(value)} tCO₂e`),
  }));

  return (
    <Paper variant='outlined' sx={{ p: 4, height: paperHeight, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      <Stack direction='row' alignItems='center'>
        <Typography variant='subtitle1' sx={{ flex: 1 }}>
          <b>{title}</b>
        </Typography>
        {tooltip && !isMobileMode && <TooltipButton text={tooltip} noPadding={true} placement='right' />}
      </Stack>
      <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ mt: 1, mb: 0.5 }}>
        {series.map((entry, index) => (
          <Stack key={entry.dataKey} direction='row' alignItems='center' spacing={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: colors[index % colors.length] }} />
            <Typography variant='caption' color='text.secondary'>{entry.label}</Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ width: '100%', height: chartHeight }}>
        <BarChart
          dataset={dataset}
          xAxis={[{ scaleType: 'band', dataKey: xAxisDataKey, tickPlacement: 'middle', tickLabelPlacement: 'middle', tickSize: 0 }]}
          yAxis={[{ disableLine: true, tickSize: 0 }]}
          series={seriesWithFormat}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          height={chartHeight}
          borderRadius={4}
          colors={colors}
          margin={{ left: 56, right: 16, top: 8, bottom: 28 }}
          axisHighlight={{ x: 'band' }}
          sx={{
            [`.${axisClasses.root}`]: {
              [`.${axisClasses.tick}, .${axisClasses.line}`]: { stroke: 'text.primary', strokeWidth: 1 },
              [`.${axisClasses.tickLabel}`]: { fill: 'text.primary' },
            },
          }}
        />
      </Box>
    </Paper>
  );
};
